/**
 * @file LegalPage.tsx
 * @summary Composant global
 *
 * Composant transverse utilisé dans plusieurs pages (header, footer, SEO, layout…).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import BookingCTA from "@/components/BookingCTA";
import SEO from "@/components/SEO";
import { buildSerpImageGraph, SERP_IMAGE_URLS } from "@/lib/serp-images";

interface Props {
  title: string;
  subtitle: string;
  remember: string[];
  children: ReactNode;
  /** SEO meta */
  seoTitle?: string;
  seoDescription?: string;
  canonicalPath: string; // e.g. "/legal/mentions"
  /** When true, this legal page is excluded from search indexing. */
  noindex?: boolean;
}

export default function LegalPage({ title, subtitle, remember, children, seoTitle, seoDescription, canonicalPath, noindex }: Props) {
  // Inject a robots noindex meta only for legal pages flagged as such
  if (noindex && typeof document !== "undefined") {
    let robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, follow");
  }
  const canonical = `https://crossfitchistera.fr${canonicalPath}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: seoTitle || title,
      description: seoDescription || subtitle,
      url: canonical,
      isPartOf: { "@type": "WebSite", name: "CrossFit Chistera", url: "https://crossfitchistera.fr" },
      publisher: { "@type": "Organization", name: "CrossFit Chistera" },
    },
    buildSerpImageGraph(),
  ];
  return (
    <div className="min-h-screen bg-background">
      <SEO title={seoTitle || `${title} — CrossFit Chistera`} description={seoDescription || subtitle} canonical={canonical} ogImages={noindex ? undefined : SERP_IMAGE_URLS} jsonLd={noindex ? jsonLd[0] : jsonLd} />
      <Header />
      <main className="pt-24 pb-20">
        <div className="container max-w-[900px] mx-auto px-4 md:px-8">
          {/* Back */}
          <Link to="/">
            <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground hover:text-gold font-body normal-case">
              <ArrowLeft size={16} className="mr-1" /> Revenir à l'accueil
            </Button>
          </Link>

          <h1 className="text-4xl md:text-5xl text-foreground mb-2">{title}</h1>
          <p className="text-muted-foreground font-body normal-case mb-8">{subtitle}</p>

          {/* À retenir */}
          <div className="bg-gold/10 border border-gold/20 rounded-lg p-6 mb-10">
            <h3 className="text-lg text-foreground mb-3">À retenir</h3>
            <ul className="text-sm text-foreground/80 space-y-1.5 font-body normal-case">
              {remember.map((r, i) => <li key={i}>✓ {r}</li>)}
            </ul>
          </div>

          {/* Content */}
          <div className="prose prose-sm max-w-none font-body normal-case text-foreground/90 leading-relaxed
            [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:text-foreground [&_h2]:mt-10 [&_h2]:mb-4
            [&_h3]:font-heading [&_h3]:text-xl [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-3
            [&_ul]:space-y-1 [&_li]:text-muted-foreground [&_a]:text-gold [&_a]:hover:underline
            [&_strong]:text-foreground
          ">
            {children}
          </div>

          <BookingCTA />

          <div className="mt-12 text-center">
            <Link to="/">
              <Button variant="outline" className="border-gold text-gold hover:bg-gold/10 rounded-lg font-body normal-case">
                Revenir à l'accueil
              </Button>
            </Link>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6 font-body normal-case">
            Version 1.0 — Dernière mise à jour : février 2026
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
